import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { message } from 'antd';
import Dialog from '@/src/components/atom/Dialog';
import DialogFooterContainer from '@/src/components/atom/Dialog/DialogFooterContainer';
import { Button } from '@/src/components/atom/Button';
import AlertDialog from '@/src/components/atom/AlertDialog';

interface RejectReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  loading?: boolean;
}

const StyledTextArea = styled.textarea`
  width: 100%;
  height: 156px;
  padding: 10px 16px;
  background: white;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  border: 1px solid #E7EDF0;
  outline: none;
  resize: none;
  font-size: 16px;
  font-family: 'Pretendard';
  font-weight: 400;
  line-height: 24px;
  color: #222729;
  
  &:focus {
    border-color: #166DD7;
  }
  
  &::placeholder {
    color: #AEB7BC;
  }
`;

const TextAreaWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const CharCounter = styled.div`
  position: absolute;
  bottom: 10px;
  right: 16px;
  color: #AEB7BC;
  font-size: 13px;
  font-family: 'Pretendard';
  font-weight: 500;
  line-height: 14px;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  
  span {
    color: #222729;
    font-size: 16px;
    font-family: 'Pretendard';
    font-weight: 500;
    line-height: 24px;
  }
  
  .required {
    color: #D64C4C;
  }
`;

const ErrorMessage = styled.div`
  color: #D64C4C;
  font-size: 13px;
  font-family: 'Pretendard';
  font-weight: 400;
  line-height: 18px;
  margin-top: 4px;
`;

export const RejectReasonModal: React.FC<RejectReasonModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(t('contract:reject.reasonRequired'));
      return;
    }
    
    // 확인 다이얼로그 표시
    setShowConfirmDialog(true);
  };

  const handleConfirmReject = async () => {
    try {
      setSubmitting(true);
      setShowConfirmDialog(false);
      await onSubmit(reason);
      message.success(t('contract:reject.completed'));
      setReason('');
      setError('');
      onClose();
    } catch (error) {
      console.error('Submit failed:', error);
      message.error(t('common:error.unknown'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setReason('');
    setError('');
    onClose();
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setReason(value);
      if (error && value.trim()) {
        setError('');
      }
    }
  };

  return (
    <>
      <Dialog
        title={t('contract:exporter.detail.dialogTitle.rejectContract')}
        open={isOpen}
        onOpenChange={(open) => !open && !showConfirmDialog && handleCancel()}
        destroyDialogWhenEscapePress={!showConfirmDialog}
        width={480}
        footer={
          <DialogFooterContainer>
            <Button
              buttonGrade="tertiary"
              buttonColor="black"
              buttonSize={40}
              onClick={handleCancel}
            >
              {t('common:cancel')}
            </Button>
            <Button
              buttonGrade="primary"
              buttonColor="blue"
              buttonSize={40}
              onClick={handleSubmit}
              disabled={!reason.trim() || submitting || loading}
            >
              {t('contract:reject.submit')}
            </Button>
          </DialogFooterContainer>
        }
      >
        <FormWrapper>
          <Label>
            <span>{t('contract:reject.reasonLabel')}</span>
            <span className="required">*</span>
          </Label>
          <TextAreaWrapper>
            <StyledTextArea
              value={reason}
              onChange={handleChange}
              placeholder={t('contract:reject.reasonPlaceholder')}
            />
            <CharCounter>{reason.length} / 100</CharCounter>
          </TextAreaWrapper>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </FormWrapper>
      </Dialog>

      {/* 반려 확인 다이얼로그 */}
      <AlertDialog
        open={showConfirmDialog}
        title={t('contract:exporter.detail.alertDialog.rejectContractTitle')}
        description={t('contract:exporter.detail.alertDialog.rejectContractConfirmDescription')}
        onOpenChange={(open) => !open && setShowConfirmDialog(false)}
        onCancel={() => setShowConfirmDialog(false)}
        onOk={handleConfirmReject}
        okText={t('common:ok')}
        cancelText={t('common:cancel')}
        isOkLoading={submitting}
      />
    </>
  );
};